import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Shield,
  Search,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  UserPlus,
  Copy,
  Fingerprint
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import { UserInfo, UserRole, CreateUserRequest, UpdateUserRequest, InviteUserRequest } from '../types/Auth';
import Header from './Header';

interface UserFormData {
  email: string;
  name: string;
  password: string;
  role: UserRole;
  permissions: string[];
}

const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetMfaConfirm, setShowResetMfaConfirm] = useState(false);
  const [showResetPasskeysConfirm, setShowResetPasskeysConfirm] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    password: '',
    role: 'user',
    permissions: []
  });
  const [formLoading, setFormLoading] = useState(false);

  // Available permissions
  const availablePermissions = [
    'read',
    'write',
    'admin'
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.getUsers(page, pageSize, searchTerm || undefined);
      setUsers(response.users);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => {
    if (currentUser?.role?.toLowerCase() === 'admin') {
      fetchUsers();
    }
  }, [currentUser, fetchUsers]);

  const handleAddUser = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'user',
      permissions: []
    });
    setShowAddModal(true);
  };

  const handleEditUser = (user: UserInfo) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '', // Don't pre-fill password
      role: (user.role?.toLowerCase() || 'user') as UserRole,
      permissions: [...user.permissions]
    });
    setShowEditModal(true);
  };

  const handleDeleteUser = (user: UserInfo) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const handleResetMfa = (user: UserInfo) => {
    setSelectedUser(user);
    setShowResetMfaConfirm(true);
  };

  const handleResetPasskeys = (user: UserInfo) => {
    setSelectedUser(user);
    setShowResetPasskeysConfirm(true);
  };

  // Page-level Cancel — closes any open modal, clears the selected user
  // state, and wipes any error banner. If nothing is open, this is a
  // no-op (effectively "there's nothing to cancel").
  const handleCancelAction = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteConfirm(false);
    setShowResetMfaConfirm(false);
    setShowResetPasskeysConfirm(false);
    setShowInviteModal(false);
    setSelectedUser(null);
    setInviteLink(null);
    setInviteCopied(false);
    setError(null);
  };

  // Derived flag: is any modal currently open? Used to enable/disable the
  // page-level Cancel button so it's only actionable when there's
  // actually something to cancel.
  const isAnyModalOpen =
    showAddModal ||
    showEditModal ||
    showDeleteConfirm ||
    showResetMfaConfirm ||
    showResetPasskeysConfirm ||
    showInviteModal;

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.name || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setFormLoading(true);
      setError(null);
      const createData: CreateUserRequest = {
        email: formData.email,
        name: formData.name,
        password: formData.password,
        role: formData.role,
        permissions: formData.permissions
      };
      await authService.createUser(createData);
      setSuccess('User created successfully');
      setShowAddModal(false);
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !formData.email || !formData.name) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setFormLoading(true);
      setError(null);
      const updateData: UpdateUserRequest = {
        id: selectedUser.id,
        email: formData.email,
        name: formData.name,
        role: formData.role,
        permissions: formData.permissions
      };
      await authService.updateUser(updateData);
      setSuccess('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      setFormLoading(true);
      setError(null);
      await authService.deleteUser(selectedUser.id);
      setSuccess('User deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmResetMfa = async () => {
    if (!selectedUser) return;

    try {
      setFormLoading(true);
      setError(null);
      await authService.resetUserMfa(selectedUser.id);
      setSuccess('MFA reset successfully');
      setShowResetMfaConfirm(false);
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset MFA');
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmResetPasskeys = async () => {
    if (!selectedUser) return;

    try {
      setFormLoading(true);
      setError(null);
      await authService.resetUserPasskeys(selectedUser.id);
      setSuccess('Passkeys reset successfully');
      setShowResetPasskeysConfirm(false);
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset passkeys');
    } finally {
      setFormLoading(false);
    }
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const [inviteFormData, setInviteFormData] = useState<{ email: string; name: string; role: UserRole; permissions: string[] }>({
    email: '',
    name: '',
    role: 'user',
    permissions: []
  });

  const handleInviteUser = () => {
    setInviteFormData({ email: '', name: '', role: 'user', permissions: [] });
    setInviteLink(null);
    setInviteCopied(false);
    setShowInviteModal(true);
  };

  const handleSubmitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteFormData.email || !inviteFormData.name) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setFormLoading(true);
      setError(null);
      const response = await authService.inviteUser({
        email: inviteFormData.email,
        name: inviteFormData.name,
        role: inviteFormData.role,
        permissions: inviteFormData.permissions
      } as InviteUserRequest);
      setInviteLink(response.inviteLink);
      setSuccess('User invited successfully');
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to invite user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCopyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    }
  };

  const handleInvitePermissionToggle = (permission: string) => {
    setInviteFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return '#ef4444';
      case 'manager':
        return '#f59e0b';
      case 'user':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  // Check if current user is admin
  if (currentUser?.role?.toLowerCase() !== 'admin') {
    return (
      <div className="dashboard">
        <Header
          title="User Management"
          showBackButton={true}
          onBack={() => navigate('/dashboard')}
        />
        <div className="error-container" style={{ padding: '2rem' }}>
          <div className="error-message">
            <AlertCircle size={48} />
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <p>Admin role required.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header
        title="User Management"
        showBackButton={true}
        onBack={() => navigate('/dashboard')}
      />

      <div className="dashboard-content" style={{ padding: '2rem' }}>
        {/* Success/Error Messages */}
        {success && (
          <motion.div
            className="success-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '1rem',
              background: '#10b981',
              color: 'white',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <CheckCircle size={20} />
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </motion.div>
        )}

        {error && (
          <motion.div
            className="error-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '1rem',
              background: '#ef4444',
              color: 'white',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <AlertCircle size={20} />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </motion.div>
        )}

        {/* Toolbar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: 1, minWidth: '300px' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
              <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.9rem'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <motion.button
              onClick={handleInviteUser}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem'
              }}
            >
              <UserPlus size={18} />
              Invite User
            </motion.button>
            <motion.button
              onClick={handleAddUser}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem'
              }}
            >
              <Plus size={18} />
              Add User
            </motion.button>
            {/* Page-level Cancel — closes any open modal and clears
                selected-user state. Disabled when nothing is open. */}
            <motion.button
              onClick={handleCancelAction}
              disabled={!isAnyModalOpen}
              whileHover={{ scale: isAnyModalOpen ? 1.05 : 1 }}
              whileTap={{ scale: isAnyModalOpen ? 0.95 : 1 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: isAnyModalOpen ? 'pointer' : 'not-allowed',
                fontWeight: '500',
                fontSize: '0.9rem',
                opacity: isAnyModalOpen ? 1 : 0.5
              }}
            >
              <X size={18} />
              Cancel
            </motion.button>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <Users size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>No users found</p>
          </div>
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Role</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Permissions</th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '1rem', fontSize: '0.9rem' }}>{user.name}</td>
                    <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>{user.email}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: `${getRoleBadgeColor(user.role)}20`,
                        color: getRoleBadgeColor(user.role)
                      }}>
                        {user.role.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                      {user.permissions.length > 0 ? (
                        <span>{user.permissions.length} permission{user.permissions.length !== 1 ? 's' : ''}</span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>No permissions</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <motion.button
                          onClick={() => handleEditUser(user)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          style={{
                            padding: '0.5rem',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#3b82f6'
                          }}
                          title="Edit user"
                        >
                          <Edit size={16} />
                        </motion.button>
                        <motion.button
                          onClick={() => handleResetMfa(user)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          style={{
                            padding: '0.5rem',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#f59e0b'
                          }}
                          title="Reset MFA"
                        >
                          <Shield size={16} />
                        </motion.button>
                        <motion.button
                          onClick={() => handleResetPasskeys(user)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          style={{
                            padding: '0.5rem',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#8b5cf6'
                          }}
                          title="Reset Passkeys"
                        >
                          <Fingerprint size={16} />
                        </motion.button>
                        {user.id !== currentUser?.id && (
                          <motion.button
                            onClick={() => handleDeleteUser(user)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            style={{
                              padding: '0.5rem',
                              background: '#f3f4f6',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              color: '#ef4444'
                            }}
                            title="Delete user"
                          >
                            <Trash2 size={16} />
                          </motion.button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginTop: '2rem'
          }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                background: page === 1 ? '#f3f4f6' : 'white',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                color: page === 1 ? '#9ca3af' : '#374151'
              }}
            >
              Previous
            </button>
            <span style={{ color: '#6b7280' }}>
              Page {page} of {Math.ceil(total / pageSize)}
            </span>
            <button
              onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
              disabled={page >= Math.ceil(total / pageSize)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                background: page >= Math.ceil(total / pageSize) ? '#f3f4f6' : 'white',
                cursor: page >= Math.ceil(total / pageSize) ? 'not-allowed' : 'pointer',
                color: page >= Math.ceil(total / pageSize) ? '#9ca3af' : '#374151'
              }}
            >
              Next
            </button>
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Add New User</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0.25rem'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleSubmitAdd}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Permissions
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {availablePermissions.map(permission => (
                      <label key={permission} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem' }}>{permission.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Cancel
                  </button>
                  <motion.button
                    type="submit"
                    disabled={formLoading}
                    whileHover={{ scale: formLoading ? 1 : 1.02 }}
                    whileTap={{ scale: formLoading ? 1 : 0.98 }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#3b82f6',
                      color: 'white',
                      cursor: formLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {formLoading ? (
                      <div className="button-spinner" style={{ width: '16px', height: '16px' }}></div>
                    ) : (
                      <Save size={16} />
                    )}
                    {formLoading ? 'Creating...' : 'Create User'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Edit User</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0.25rem'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleSubmitEdit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Permissions
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {availablePermissions.map(permission => (
                      <label key={permission} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem' }}>{permission.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedUser(null);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Cancel
                  </button>
                  <motion.button
                    type="submit"
                    disabled={formLoading}
                    whileHover={{ scale: formLoading ? 1 : 1.02 }}
                    whileTap={{ scale: formLoading ? 1 : 0.98 }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#3b82f6',
                      color: 'white',
                      cursor: formLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {formLoading ? (
                      <div className="button-spinner" style={{ width: '16px', height: '16px' }}></div>
                    ) : (
                      <Save size={16} />
                    )}
                    {formLoading ? 'Updating...' : 'Update User'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && selectedUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '400px'
              }}
            >
              <div style={{ marginBottom: '1.5rem' }}>
                <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '1rem' }} />
                <h2 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
                  Delete User
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                  Are you sure you want to delete <strong>{selectedUser.name}</strong> ({selectedUser.email})? This action cannot be undone.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedUser(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleConfirmDelete}
                  disabled={formLoading}
                  whileHover={{ scale: formLoading ? 1 : 1.02 }}
                  whileTap={{ scale: formLoading ? 1 : 0.98 }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#ef4444',
                    color: 'white',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {formLoading ? (
                    <div className="button-spinner" style={{ width: '16px', height: '16px' }}></div>
                  ) : (
                    <Trash2 size={16} />
                  )}
                  {formLoading ? 'Deleting...' : 'Delete'}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Reset MFA Confirmation Modal */}
        {showResetMfaConfirm && selectedUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '400px'
              }}
            >
              <div style={{ marginBottom: '1.5rem' }}>
                <Shield size={48} style={{ color: '#f59e0b', marginBottom: '1rem' }} />
                <h2 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
                  Reset MFA
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                  Are you sure you want to reset MFA for <strong>{selectedUser.name}</strong> ({selectedUser.email})? They will need to set up MFA again on their next login.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowResetMfaConfirm(false);
                    setSelectedUser(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleConfirmResetMfa}
                  disabled={formLoading}
                  whileHover={{ scale: formLoading ? 1 : 1.02 }}
                  whileTap={{ scale: formLoading ? 1 : 0.98 }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#f59e0b',
                    color: 'white',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {formLoading ? (
                    <div className="button-spinner" style={{ width: '16px', height: '16px' }}></div>
                  ) : (
                    <Shield size={16} />
                  )}
                  {formLoading ? 'Resetting...' : 'Reset MFA'}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
        {/* Reset Passkeys Confirmation Modal */}
        {showResetPasskeysConfirm && selectedUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '400px'
              }}
            >
              <div style={{ marginBottom: '1.5rem' }}>
                <Fingerprint size={48} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
                <h2 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
                  Reset Passkeys
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                  Are you sure you want to reset passkeys for <strong>{selectedUser.name}</strong> ({selectedUser.email})? All registered passkeys will be removed and the user will need to register new ones.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowResetPasskeysConfirm(false);
                    setSelectedUser(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleConfirmResetPasskeys}
                  disabled={formLoading}
                  whileHover={{ scale: formLoading ? 1 : 1.02 }}
                  whileTap={{ scale: formLoading ? 1 : 0.98 }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#8b5cf6',
                    color: 'white',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {formLoading ? (
                    <div className="button-spinner" style={{ width: '16px', height: '16px' }}></div>
                  ) : (
                    <Fingerprint size={16} />
                  )}
                  {formLoading ? 'Resetting...' : 'Reset Passkeys'}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Invite User Modal */}
        {showInviteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Invite User</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0.25rem'
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {inviteLink ? (
                <div>
                  <p style={{ marginBottom: '1rem', color: '#374151' }}>
                    User invited successfully! Share this link with the user to complete their account setup:
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        fontSize: '0.85rem',
                        color: '#374151',
                        outline: 'none'
                      }}
                    />
                    <motion.button
                      onClick={handleCopyInviteLink}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '0.5rem',
                        background: inviteCopied ? '#10b981' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap' as const
                      }}
                    >
                      <Copy size={14} />
                      {inviteCopied ? 'Copied!' : 'Copy'}
                    </motion.button>
                  </div>
                  <button
                    onClick={() => setShowInviteModal(false)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitInvite}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={inviteFormData.email}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={inviteFormData.name}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                      Role *
                    </label>
                    <select
                      value={inviteFormData.role}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, role: e.target.value as UserRole })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="user">User</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                      Permissions
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {availablePermissions.map(permission => (
                        <label key={permission} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={inviteFormData.permissions.includes(permission)}
                            onChange={() => handleInvitePermissionToggle(permission)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '0.9rem' }}>{permission.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                    No password required. The user will receive a link to set their password.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setShowInviteModal(false)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Cancel
                    </button>
                    <motion.button
                      type="submit"
                      disabled={formLoading}
                      whileHover={{ scale: formLoading ? 1 : 1.02 }}
                      whileTap={{ scale: formLoading ? 1 : 0.98 }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        borderRadius: '6px',
                        background: '#10b981',
                        color: 'white',
                        cursor: formLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {formLoading ? (
                        <div className="button-spinner" style={{ width: '16px', height: '16px' }}></div>
                      ) : (
                        <UserPlus size={16} />
                      )}
                      {formLoading ? 'Sending...' : 'Send Invite'}
                    </motion.button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;

